import { useCallback, useMemo } from "react";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { InvoiceSource } from "../../../../api/types/expenseManagementTypes";
import useClientContext from "../../../../context/useClientContext";
import type { InvoiceCardSource } from "./invoiceCardSource";
import EInvoiceDetails from "./EInvoiceDetails";

interface InvoiceSourceState {
  source?: InvoiceCardSource;
  isLoading: boolean;
  hasFailed: boolean;
  refresh: () => Promise<void>;
}

const getSource = (invoiceSource: InvoiceSource): InvoiceCardSource => ({
  label: "E-invoice",
  description:
    "Created from the vendor's e-invoice. Values read from it can't be edited. If one is wrong, ask the vendor for a corrected e-invoice.",
  readOnlyFieldIds: invoiceSource.readOnlyFieldIds,
  relatedObjectType: "InvoiceLine",
  readOnlyRelatedFieldIds: invoiceSource.readOnlyRelatedFieldIds,
  workflowActionsWithChecks: invoiceSource.workflowActionsWithChecks,
  details: invoiceSource.unmatchedBuyerName ? (
    <EInvoiceDetails buyerName={invoiceSource.unmatchedBuyerName} />
  ) : undefined,
});

const useInvoiceSource = (invoiceId: string): InvoiceSourceState => {
  const { isPortalFeatureEnabled, hasAnyPermission } = useClientContext();

  const isEnabled =
    isPortalFeatureEnabled("ExpenseManagementEInvoicingEnabled") &&
    hasAnyPermission(["ViewExpenses", "ManageExpenses"]);

  const getInvoiceSource = useCallback(() => adminApi.getInvoiceSource(invoiceId), [invoiceId]);
  const [response, error, { isFetching, fetchSilent }] = useFetch(getInvoiceSource, { disabled: !isEnabled });

  const invoiceSource = isEnabled ? response?.source : undefined;
  const source = useMemo(() => (invoiceSource ? getSource(invoiceSource) : undefined), [invoiceSource]);

  const hasFailed = isEnabled && error !== undefined && error.code !== 403 && error.type !== "NotFound";
  if (hasFailed) {
    logError(error, "[useInvoiceSource] getInvoiceSource");
  }

  const refresh = useCallback(async () => {
    const refreshError = await fetchSilent();
    if (refreshError) {
      logError(refreshError, "[useInvoiceSource] refresh");
    }
  }, [fetchSilent]);

  return { source, isLoading: isEnabled && isFetching, hasFailed, refresh };
};

export default useInvoiceSource;
